// utils/activeTag.js
//
// THE ACTIVE TAG, the client's pure half (docs/plans/tags-active-tag-proposal.md;
// the owner's build calls b46–b52, 2026-09-30; keepr-api docs/SCHEMA.md § The
// active tag). One of the person's own private tags that their own forms put
// on every new item while it is on — a personal preference kept on the server
// (`userPreferences.prefs.activeTag { tagId, since, until? }`), so it follows
// them across devices. The server never applies it: the form shows it on its
// Tags row before the save, removable for that one item, and puts it on
// through the person's own store after the create (T4b, T9).
//
// The words are ruled (b52 and the tags words): "Make active…", "Stop adding
// it", "Adding to *tag*", "Your active tag".

import dayjs from 'dayjs';

import { formatShortDate } from './dateDisplay';
import { normalizeId } from './ids';

export const MAKE_ACTIVE_LABEL = 'Make active…';
export const STOP_ADDING_LABEL = 'Stop adding it';
export const ADDING_TO_LABEL = 'Adding to';
export const ACTIVE_TAG_NOTE = 'Your active tag';

/**
 * The stored preference as the client reads it: `{ tagId, since, until }`
 * (`until` null when it has none), or null — nothing stored, a shape this
 * build does not know, or an end that has passed (the API answers it off
 * too, and clears it; this is for the minutes between reads).
 */
export function readActiveTag(raw, { now = Date.now() } = {}) {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
	// Ids are lower-case hex on the wire; the API stores this one so too.
	const tagId = (normalizeId(raw.tagId) || '').toLowerCase();
	if (!tagId) return null;
	const until = raw.until ? dayjs(raw.until) : null;
	if (until && until.isValid() && until.valueOf() <= now) return null;
	return {
		tagId,
		since: raw.since || null,
		until: until && until.isValid() ? raw.until : null,
	};
}

/** "since Sep 29 · until Oct 31" — the chip's second line; "since Sep 29" with no end. */
export function activeTagDates(active) {
	if (!active) return '';
	const parts = [];
	const since = formatShortDate(active.since);
	if (since) parts.push(`since ${since}`);
	const until = formatShortDate(active.until);
	if (until) parts.push(`until ${until}`);
	return parts.join(' · ');
}

/**
 * The end the dialog's date picker means: the LAST moment of that day where
 * the person is ("until Oct 31" includes Oct 31), as an ISO instant — the API
 * takes a date and time, and refuses one that is not in the future.
 */
export function untilEndOfDay(day) {
	if (!day) return null;
	const d = dayjs(day);
	return d.isValid() ? d.endOf('day').toISOString() : null;
}

/** May the date picker offer this day? Today and later only. */
export const isPastDay = day => Boolean(day) && dayjs(day).endOf('day').isBefore(dayjs());

/** A refused PATCH, as a sentence the dialog or the chip can say. */
export function activeTagRefusal(error, fallback = 'Your active tag could not be changed. Please try again.') {
	if (!error?.response) return 'We couldn’t reach the server. Check your connection and try again.';
	const data = error.response.data || {};
	switch (data.code) {
		case 'unknown_tag':
			return 'That tag can’t be your active tag: it may have been deleted. Only your own private tags can be active.';
		case 'invalid_active_tag':
			return data.field === 'activeTag.until' ? 'Pick an end date that is today or later.' : fallback;
		case 'unconfined_key_required':
			return data.message || fallback;
		default:
			return fallback;
	}
}
